// src/components/FeedbackModal.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { feedbackService } from '../services/feedbackService';

const FeedbackModal = ({ isOpen, onClose, onSubmitSuccess, user }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [category, setCategory] = useState('general');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const categories = [
    { value: 'general', label: 'General Feedback', icon: 'fa-comment' },
    { value: 'facilities', label: 'Facilities', icon: 'fa-building' },
    { value: 'events', label: 'Events', icon: 'fa-calendar-alt' },
    { value: 'booking', label: 'Booking Experience', icon: 'fa-calendar-check' },
    { value: 'staff', label: 'Staff Service', icon: 'fa-user-tie' },
    { value: 'suggestion', label: 'Suggestion', icon: 'fa-lightbulb' },
    { value: 'complaint', label: 'Complaint', icon: 'fa-exclamation-triangle' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }
    
    if (!comment.trim()) {
      setError('Please enter your feedback');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const result = await feedbackService.submitFeedback(rating, comment, category);
      
      if (result.success) {
        setSubmitted(true);
        if (onSubmitSuccess) onSubmitSuccess();
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setError(result.message || 'Failed to submit feedback');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleClose = () => {
    setRating(0);
    setHoverRating(0);
    setComment('');
    setCategory('general');
    setSubmitted(false);
    setError('');
    onClose();
  };
  
  const getStarIcon = (starValue) => {
    if (hoverRating >= starValue) {
      return 'fas fa-star';
    }
    if (rating >= starValue) {
      return 'fas fa-star';
    }
    return 'far fa-star';
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="feedback-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className="feedback-modal"
            initial={{ scale: 0.9, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 30 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            {!submitted ? (
              <>
                <div className="feedback-modal-header">
                  <div className="feedback-modal-icon">
                    <i className="fas fa-star"></i>
                  </div>
                  <h3>Share Your Feedback</h3>
                  <button className="feedback-modal-close" onClick={handleClose}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                
                <div className="feedback-modal-body">
                  {/* User Info Display */}
                  <div className="feedback-user-info">
                    <div className="user-avatar">
                      {user?.full_name?.charAt(0) || user?.first_name?.charAt(0) || 'U'}
                    </div>
                    <div className="user-details">
                      <span className="user-name">{user?.full_name || `${user?.first_name} ${user?.last_name}`}</span>
                      <span className="user-email">{user?.email}</span>
                    </div>
                  </div>
                  
                  {/* Category Selection */}
                  <div className="feedback-category-section">
                    <label>Category</label>
                    <div className="category-buttons">
                      {categories.map(cat => (
                        <button
                          key={cat.value}
                          type="button"
                          className={`category-btn ${category === cat.value ? 'active' : ''}`}
                          onClick={() => setCategory(cat.value)}
                        >
                          <i className={`fas ${cat.icon}`}></i>
                          <span>{cat.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Star Rating */}
                  <div className="feedback-rating-section">
                    <label>Your Rating</label>
                    <div className="stars-container">
                      {[1, 2, 3, 4, 5].map(star => (
                        <i
                          key={star}
                          className={getStarIcon(star)}
                          onMouseEnter={() => setHoverRating(star)}
                          onMouseLeave={() => setHoverRating(0)}
                          onClick={() => setRating(star)}
                        ></i>
                      ))}
                      <span className="rating-text">
                        {rating === 1 && 'Poor'}
                        {rating === 2 && 'Fair'}
                        {rating === 3 && 'Good'}
                        {rating === 4 && 'Very Good'}
                        {rating === 5 && 'Excellent'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Comment Input */}
                  <div className="feedback-comment-section">
                    <label>Your Feedback</label>
                    <textarea
                      rows="5"
                      placeholder="Tell us about your experience... What did you like? What could we improve?"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                    ></textarea>
                  </div>
                  
                  {/* Error Message */}
                  {error && (
                    <div className="feedback-error">
                      <i className="fas fa-exclamation-circle"></i>
                      <span>{error}</span>
                    </div>
                  )}
                </div>
                
                <div className="feedback-modal-footer">
                  <button className="btn-cancel" onClick={handleClose}>
                    Cancel
                  </button>
                  <button 
                    className="btn-submit" 
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane"></i>
                        Submit Feedback
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="feedback-success">
                <div className="success-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <h3>Thank You!</h3>
                <p>Your feedback has been submitted successfully. We appreciate your input!</p>
                <button className="btn-close-success" onClick={handleClose}>
                  Close
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FeedbackModal;